const pbjs = require("prebid.js").default;
import 'prebid.js/modules/consentManagementTcf.js';

import type {HeaderBiddingConfig} from "header-bidding-client-base/modules/config/domain/headerBiddingConfig";
import type {GlobalConfig} from "header-bidding-client-base/modules/config/domain/globalConfig";
import type {ILogger} from "ads-client-base/modules/logger/domain/iLogger";
import {
    PrebidJSAnalyticsModule
} from "header-bidding-client-base/modules/analytics/infrastructure/prebid-js/prebidJSAnalyticsModule";
import {HeaderBiddingPlatforms} from "header-bidding-base/modules/header-bidding/domain/platforms";

import {
    IAdUnitTypeDTO,
    VideoAdUnitContext,
    VideoAdUnitPlacement,
    VideoAdUnitPosition
} from "services-comun-meteored/modules/services/header-bidding/interface";
import type {PrebidJsBaseBiddersLoader, PrebidJsBidderAdUnit} from "./bidders/prebidJsBaseBiddersLoader";
import type {AdUnits} from "../../domain/headerBiddingService";
import type {IHeaderBiddingPlatform, VideoInstreamBidData} from "../../domain/headerBiddingPlatform";

declare global {
    interface Window {
        pbjs?: any;
    }
}

export interface PrebidJsAdUnit<BidderAdUnitDef = unknown> {
    code: string;
    mediaTypes: {
        banner?: {
            sizes: number[][];
        };
        video?: {
            pos?: number;
            context?: string;
            useCacheKey?: boolean;
            placement?: number;
            plcmt?: number;
            playerSize?: number[];
            api?: number[];
            mimes?: string[];
            protocols?: number[];
            playbackmethod?: number[];
            minduration?: number;
            maxduration?: number;
            w?: number;
            h?: number;
            startdelay?: number;
            linearity?: number;
            skip?: number;
            skipmin?: number;
            skipafter?: number;
            minbitrate?: number;
            maxbitrate?: number;
            delivery?: number[];
            playbackend?: number;
        };
    },
    bids: PrebidJsBidderAdUnit[];
    ortb2Imp?: any;
}

export class PrebidJSAdapter implements IHeaderBiddingPlatform<PrebidJsAdUnit> {
    private adUnits: PrebidJsAdUnit[];
    private bidTimeout: number;

    public constructor(
        private config: HeaderBiddingConfig,
        private globalConfig: GlobalConfig,
        private logger: ILogger,
        private readonly adUnitMaps: AdUnits = {},
        private biddersLoader: PrebidJsBaseBiddersLoader,
    ) {
        this.adUnits = [];
        this.bidTimeout = config.platformsConfig[HeaderBiddingPlatforms.PREBID]?.timeout || this.config.timeout;
    }

    public get platform() {
        return HeaderBiddingPlatforms.PREBID;
    }

    public async init() {
        this.logger.log("[Prebid.js]", "Initializing...");

        await this.biddersLoader.registerBidders(this.config);

        pbjs.processQueue();

        this.setPrebidJSConfig();

        this.setPrebidJSBiddersSettings();

        this.setPrebidJSEventListeners();

        if (this.globalConfig.analytics) {
            const analyticsModule = new PrebidJSAnalyticsModule(pbjs, this.logger);
            analyticsModule.init(this.globalConfig, this.adUnitMaps);
        }

        this.logger.log("[Prebid.js]", "Initialized");
    }

    public addAdUnits(adUnits: {id: number, adUnitPath: string, divId: string, type: IAdUnitTypeDTO}[]) {
        const adUnitsParsed: PrebidJsAdUnit[] = adUnits.map((adUnit) => {
            const adUnitExists = this.getAdUnit(adUnit.divId);
            if (adUnitExists) {
                this.logger.warn("[Prebid.js]", "Adunit already inserted", adUnit.divId);
                return;
            }

            const bids = this.biddersLoader.getBiddersAdUnits(adUnit.id, adUnit.adUnitPath);
            if (bids instanceof Error) {
                this.logger.warn("[Prebid.js]", bids.message);
                return;
            }

            let videoProps: any = {};
            if (adUnit.type.video) {
                if (adUnit.type.video.position) videoProps['pos'] = this.mapVideoAdUnitPosition(adUnit.type.video.position);
                if (adUnit.type.video.context) videoProps['context'] = this.mapVideoAdUnitContext(adUnit.type.video.context);
                if (adUnit.type.video.placement) videoProps['placement'] = this.mapVideoAdUnitPlacement(adUnit.type.video.placement);
                if (adUnit.type.video.player_size) videoProps['playerSize'] = adUnit.type.video.player_size;
                if (adUnit.type.video.api) videoProps['api'] = adUnit.type.video.api;
                if (adUnit.type.video.mimes) videoProps['mimes'] = adUnit.type.video.mimes;
                if (adUnit.type.video.protocols) videoProps['protocols'] = adUnit.type.video.protocols;
                if (adUnit.type.video.playback_method) videoProps['playbackmethod'] = adUnit.type.video.playback_method;
                if (adUnit.type.video.duration?.min) videoProps['minduration'] = adUnit.type.video.duration.min;
                if (adUnit.type.video.duration?.max) videoProps['maxduration'] = adUnit.type.video.duration.max;
                if (adUnit.type.video.size?.width) videoProps['w'] = adUnit.type.video.size.width;
                if (adUnit.type.video.size?.height) videoProps['h'] = adUnit.type.video.size.height;
                if (adUnit.type.video.start_delay) videoProps['startdelay'] = adUnit.type.video.start_delay;
                if (adUnit.type.video.linearity) videoProps['linearity'] = adUnit.type.video.linearity;
                if (adUnit.type.video.bitrate?.min) videoProps['minbitrate'] = adUnit.type.video.bitrate.min;
                if (adUnit.type.video.bitrate?.max) videoProps['maxbitrate'] = adUnit.type.video.bitrate.max;
            }

            const adUnitPathSlash = adUnit.adUnitPath.startsWith("/") ? adUnit.adUnitPath : "/" + adUnit.adUnitPath;
            const prebidAdUnit: PrebidJsAdUnit = {
                code: adUnit.divId,
                mediaTypes: {
                    ...adUnit.type.banner && {
                        banner: {
                            sizes: adUnit.type.banner.sizes!,
                        }
                    },
                    ...adUnit.type.video && {
                        video: videoProps,
                    }
                },
                bids: bids,
                ortb2Imp: {
                    ext: {
                        gpid: adUnitPathSlash+'#'+adUnit.divId,
                        data: {
                            adserver: {
                                name: "gam",
                                adslot: adUnitPathSlash,
                            },
                            pbadslot: adUnitPathSlash+'#'+adUnit.divId,
                            divId: adUnit.divId,
                            placement: adUnitPathSlash,
                        }
                    }
                }
            };

            this.logger.log("[Prebid.js]", "Adunit " + adUnit.adUnitPath + " loaded for " + bids.map(bid => bid.bidder).join(", "), prebidAdUnit);

            return prebidAdUnit;
        }).filter(adUnit => !!adUnit) as PrebidJsAdUnit[];

        this.adUnits = this.adUnits.concat(adUnitsParsed);

        pbjs.addAdUnits(adUnitsParsed);

        this.logger.log("[Prebid.js]", "Adunits added");

        return adUnitsParsed;
    }

    public getAdUnit(id: string) {
        return this.adUnits.find(adUnit => adUnit.code === id);
    }

    public removeAdUnits(adUnitIds: string[]): void {
        this.adUnits = this.adUnits.filter(adUnit => !adUnitIds.includes(adUnit.code));
        pbjs.removeAdUnit(adUnitIds);
        this.logger.log("[Prebid.js]", "Adunits removed", adUnitIds);
    }

    public async requestBids(adUnitCodes: string[], disabledBidders?: { [bidder: string]: string[] }) {
        this.logger.log("[Prebid.js]", "Requesting bids " + (adUnitCodes ? "for " + adUnitCodes.join(", ") : "") + "...");

        if (adUnitCodes.length > 0) {
            adUnitCodes = adUnitCodes.filter(adUnitCode => this.getAdUnit(adUnitCode));
            if (adUnitCodes.length === 0) {
                return;
            }
        }

        let adUnits = adUnitCodes.length > 0 ? this.adUnits.filter(adUnit => adUnitCodes.includes(adUnit.code)) : this.adUnits;

        if (disabledBidders) {
            Object.keys(disabledBidders).forEach((bidder: string) => {
                if (disabledBidders[bidder].length > 0) {
                    adUnits = adUnits.map(adUnit => ({
                        ...adUnit,
                        bids: disabledBidders[bidder].includes(adUnit.code) ? adUnit.bids.filter(bid => bid.bidder !== bidder) : adUnit.bids,
                    }));
                }
            });
        }

        const bids = await pbjs.requestBids({
            adUnits: adUnits,
            timeout: this.bidTimeout,
        });

        if (bids.bids && !bids.timedOut) {
            pbjs.setTargetingForGPTAsync && pbjs.setTargetingForGPTAsync();
        }

        const videoInstreamBidsData = this.getVideoInstreamBidsData(adUnitCodes, bids.timedOut);

        this.logger.log("[Prebid.js]", "Bids requested");
        this.logger.log("[Prebid.js]", bids);

        return { videoInstreamBidsData };
    }

    private setPrebidJSConfig(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const pbjsDebug = urlParams.get("pbjs_debug");
        const debug = this.globalConfig.env === "development" || pbjsDebug === "true";

        const secondaryBidders: string[] = [];
        if (this.config.biddersConfig) {
            Object.keys(this.config.biddersConfig).forEach((bidder: any) => {
                if ((this.config.biddersConfig as any)[bidder]?.secondary_bidder) {
                    secondaryBidders.push(bidder);
                }
            });
        }

        const prebidJsConfig = {
            bidderTimeout: this.bidTimeout,
            priceGranularity: "dense",
            auctionOptions: { secondaryBidders: secondaryBidders },
            bidderSequence: secondaryBidders.length ? "fixed" : "random",
            debug: debug,
            enableSendAllBids: false,
            enableTIDs: true,
            consentManagement: {
                gdpr: {
                    cmpApi: 'iab',
                    timeout: 10000,
                    defaultGdprScope: true
                }
            },
            rubicon: { singleRequest: true },
            cache: {
                url: 'https://prebid.adnxs.com/pbc/v1/cache',
            },
            userSync: {
                aliasSyncEnabled: true,
                filterSettings: {
                    iframe: {
                        bidders: '*',
                        filter: 'include'
                    },
                    image: {
                        bidders: '*',
                        filter: 'include'
                    }
                }
            },
            /*debugging: {
                enabled: true,
                intercept: [
                    {
                        when: {
                            bidder: 'sparteo',
                        },
                        then: {
                            cpm: 4.00
                        }
                    },
                ]
            }*/
        };

        pbjs.setConfig(prebidJsConfig);
        this.logger.log("[Prebid.js]", "Config set", prebidJsConfig);
    }

    private setPrebidJSBiddersSettings(): void {
        pbjs.bidderSettings = {
            standard: {
                storageAllowed: true,
                adserverTargeting: this.config.platformsConfig[HeaderBiddingPlatforms.PREBID]?.adserverTargeting || undefined,
            },
            ...this.biddersLoader.getBiddersSettings(),
        };
        this.logger.log("[Prebid.js]", "Bidder settings set", pbjs.bidderSettings);
    }

    private setPrebidJSEventListeners(): void {
        pbjs.onEvent('bidResponse', function(bid: any) {
            if ((bid.ad && bid.ad.includes('linktracer.xyz')) || (bid.adUrl && bid.adUrl.includes('linktracer.xyz'))) {
                console.warn('Bid bloqueado por contener linktracer.xyz', bid);
                bid.cpm = 0;
                bid.ad = '';
            }
        });
        this.logger.log("[Prebid.js]", "Event listeners set");
    }

    private mapVideoAdUnitPosition(position: VideoAdUnitPosition): number {
        switch (position) {
            case VideoAdUnitPosition.UNKNOWN: return 0;
            case VideoAdUnitPosition.ABOVE_THE_FOLD: return 1;
            case VideoAdUnitPosition.BELOW_THE_FOLD: return 3;
            case VideoAdUnitPosition.HEADER: return 4;
            case VideoAdUnitPosition.FOOTER: return 5;
            case VideoAdUnitPosition.SIDEBAR: return 6;
            case VideoAdUnitPosition.FULLSCREEN: return 7;
            default: return 0;
        }
    }

    private mapVideoAdUnitContext(context: VideoAdUnitContext): string {
        return context;
    }

    private mapVideoAdUnitPlacement(placement: VideoAdUnitPlacement): number | undefined {
        switch (placement) {
            case VideoAdUnitPlacement.IN_STREAM: return 1;
            case VideoAdUnitPlacement.IN_BANNER: return 2;
            case VideoAdUnitPlacement.IN_ARTICLE: return 3;
            case VideoAdUnitPlacement.IN_FEED: return 4;
            case VideoAdUnitPlacement.INETRSITIAL: return 5;
            default: return undefined;
        }
    }

    private getVideoInstreamBidsData(adUnitCodes: string[], timedOut?: boolean): VideoInstreamBidData[] {
        const videoAdUnits = adUnitCodes.map(adUnitCode => this.getAdUnit(adUnitCode)).filter(adUnit => adUnit?.mediaTypes.video) as PrebidJsAdUnit[];
        if (!videoAdUnits || !videoAdUnits.length) {
            return [];
        }

        return videoAdUnits.map(adUnit => {
            const adUnitCode = adUnit.code;

            if (timedOut) {
                return {
                    adUnitCode: adUnitCode,
                    state: 'timeout',
                };
            }

            const bids = pbjs.getHighestCpmBids(adUnitCode);
            if (bids.length) {
                const bid = bids[0];
                const vastUrl = bid.vastUrl;
                if (vastUrl) {
                    const bidderCode = bid.bidderCode;
                    const adServerTargeting = bid.adserverTargeting;

                    return { adUnitCode, state: 'bid', data: { bidderCode, vastUrl, adServerTargeting } };
                }
            }

            return {
                adUnitCode: adUnitCode,
                state: 'nobid',
            };

        });
    }
}
